import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import api from "../lib/api";
import { num } from "../lib/format";
import { useCountry } from "../context/CountryContext";

const AXIS = { fontSize: 11, fontFamily: "JetBrains Mono", fill: "#475569" };
const TIP = { border: "1px solid #CBD5E1", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 };
const TYPES = [
  { key: "loading", label: "Loading", color: "#006f9e" },
  { key: "evacuation", label: "Evacuation", color: "#84cc16" },
  { key: "trip", label: "Trip", color: "#e84e0f" },
  { key: "other", label: "Other", color: "#94a3b8" },
];
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
const money = (b) => Object.entries(b || {}).map(([c, v]) => `${c} ${num(v)}`).join(" · ") || "—";

const Card = ({ eyebrow, title, testid, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 card-lift" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold mb-1">{eyebrow}</div>
    <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep mb-4">{title}</h3>
    {children}
  </div>
);

const RankChart = ({ data, dataKey, color, tooltipExtra }) => (
  <div className="h-80">
    <ResponsiveContainer width="100%" height="100%" minHeight={300} minWidth={200} debounce={50}>
      <BarChart data={data} layout="vertical" margin={{ left: 100, right: 16, top: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
        <YAxis type="category" dataKey={dataKey} tick={AXIS} tickLine={false} axisLine={false} width={100} />
        <Tooltip contentStyle={TIP} cursor={{ fill: "rgba(0,111,158,0.05)" }}
          labelFormatter={(l, p) => (p?.[0] ? `${l} — ${tooltipExtra(p[0].payload)}` : l)}
          formatter={(v, _n, p) => [`${num(v)} trips · ${p.payload.activities} jobs`, "Trips"]} />
        <Bar dataKey="trips" name="Trips" fill={color} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const TripCharts = () => {
  const { filterParam } = useCountry();
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/reports/trips-overview", { params: { months, ...(filterParam ? { country: filterParam } : {}) } })
      .then(({ data: d }) => alive && setData(d));
    return () => { alive = false; };
  }, [months, filterParam]);

  const monthly = (data?.monthly || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  return (
    <section className="px-8 pb-10 space-y-6" data-testid="trip-charts">
      <div className="flex items-end justify-between flex-wrap gap-3 pt-2 border-t border-slate-200">
        <div className="pt-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Trips analytics</div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight text-ps-deep">
            {num(data?.total_trips || 0)} trips · last {months} months
          </h2>
        </div>
        <div className="flex border border-slate-300 rounded-md overflow-hidden" data-testid="trip-charts-months">
          {[3, 6, 12].map((n) => (
            <button key={n} onClick={() => setMonths(n)} data-testid={`trip-charts-months-${n}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${months === n ? "bg-ps-blue text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
              {n} mo
            </button>
          ))}
        </div>
      </div>

      <Card eyebrow="Monthly" title="Trips per month by activity" testid="chart-trips-per-month">
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%" minHeight={300} minWidth={200} debounce={50}>
            <BarChart data={monthly} margin={{ left: 0, right: 16, top: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TIP} cursor={{ fill: "rgba(0,111,158,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {TYPES.map((t, i) => (
                <Bar key={t.key} dataKey={t.key} name={t.label} stackId="a" fill={t.color} radius={i === TYPES.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card eyebrow="Top 10" title="Clients by trips" testid="chart-top-clients">
          <RankChart data={data?.top_clients || []} dataKey="client" color="#006f9e" tooltipExtra={(p) => money(p.billable)} />
          <div className="mt-4 divide-y divide-slate-100 text-xs" data-testid="top-clients-list">
            {(data?.top_clients || []).map((c) => (
              <div key={c.client} className="flex justify-between py-1.5 gap-3">
                <span className="font-semibold text-ps-deep truncate">{c.client}</span>
                <span className="font-mono-data text-lime-700 shrink-0">{money(c.billable)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card eyebrow="Top 10" title="Busiest trucks" testid="chart-top-trucks">
          <RankChart data={data?.top_trucks || []} dataKey="vehicle_number" color="#e84e0f" tooltipExtra={(p) => (p.clients || []).join(", ")} />
        </Card>
      </div>
    </section>
  );
};
